/* tslint:disable */
/* eslint-disable */
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull web'
 *
 * Required dependencies: @amplitude/analytics-browser@^1.2.0
 * Tracking Plan Version: 4
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli-v2
 *
 * [View Tracking Plan](https://data.amplitude.com/co2ai-pe/PE/events/main/latest)
 *
 * [Full Setup Instructions](https://data.amplitude.com/co2ai-pe/PE/implementation/web)
 */

import * as amplitude from '@amplitude/analytics-browser';

export type Environment = 'development' | 'production';

export const ApiKey: Record<Environment, string> = {
  development: '',
  production: '75deae70beabdc32cc0d5edce9a0e1ab'
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultConfiguration: BrowserOptions = {
  plan: {
    version: '4',
    branch: 'main',
    source: 'web',
    versionId: '50fe4263-b74d-4971-b86d-c7996e09a552'
  }
};

export interface LoadOptions {
  environment?: Environment;
  disabled?: boolean;
  client?: {
    apiKey?: string;
    configuration?: BrowserOptions;
    instance?: BrowserClient;
  }
}

export interface IdentifyProperties {
  initial_fbclid?: {
    [k: string]: any;
  };
  initial_gclid?: {
    [k: string]: any;
  };
  initial_referrer?: {
    [k: string]: any;
  };
  initial_referring_domain?: {
    [k: string]: any;
  };
  initial_utm_campaign?: {
    [k: string]: any;
  };
  initial_utm_content?: {
    [k: string]: any;
  };
  initial_utm_medium?: {
    [k: string]: any;
  };
  initial_utm_source?: {
    [k: string]: any;
  };
  initial_utm_term?: {
    [k: string]: any;
  };
  organisationId?: {
    [k: string]: any;
  };
  referrer?: {
    [k: string]: any;
  };
  referring_domain?: {
    [k: string]: any;
  };
}

export interface ClickEventProperties {
  buttonText: string;
  clickPageUrl: string;
}

export interface PageViewProperties {
  /**
   * | Rule | Value |
   * |---|---|
   * | Regex |  |
   */
  pageTitle: string;
  /**
   * | Rule | Value |
   * |---|---|
   * | Regex |  |
   */
  pageUrl: string;
}

export class Identify implements BaseEvent {
  event_type = 'Identify';

  constructor(
    public event_properties?: IdentifyProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class Group implements BaseEvent {
  event_type = 'Group';
}

export class ClickEvent implements BaseEvent {
  event_type = 'Click Event';

  constructor(
    public event_properties: ClickEventProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class PageView implements BaseEvent {
  event_type = 'Page View';

  constructor(
    public event_properties: PageViewProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export type PromiseResult<T> = { promise: Promise<T | void> };

const getVoidPromiseResult = () => ({ promise: Promise.resolve() });

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: BrowserClient;

  get client(): BrowserClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.amplitude) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   *
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options?: LoadOptions): PromiseResult<void> {
    this.disabled = options?.disabled ?? false;

    if (this.amplitude) {
      console.warn('WARNING: Ampli is already intialized. Ampli.load() should be called once at application startup.');
      return getVoidPromiseResult();
    }

    const env = options?.environment ?? 'development';
    const apiKey = options?.client?.apiKey ?? ApiKey[env];

    if (options?.client?.instance) {
      this.amplitude = options?.client?.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.createInstance();
      return this.amplitude.init(apiKey, undefined, { ...DefaultConfiguration, ...options?.client?.configuration });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }

    return getVoidPromiseResult();
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param properties The user properties.
   * @param options Optional event options.
   */
  identify(
    userId: string | undefined,
    properties?: IdentifyProperties,
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    if (userId) {
      options = {...options,  user_id: userId};
    }

    const amplitudeIdentify = new amplitude.Identify();
    const eventProperties = properties;
    if (eventProperties != null) {
      for (const [key, value] of Object.entries(eventProperties)) {
        amplitudeIdentify.set(key, value);
      }
    }
    return this.amplitude!.identify(
      amplitudeIdentify,
      options,
    );
  }

  /**
   * Set Group for the current user
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   */
  setGroup(groupType: string, groupName: string | string[], options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.setGroup(groupType, groupName, options);
  }

  /**
   * Identify a group and set group properties.
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   */
  groupIdentify(
    groupType: string,
    groupName: string | string[],
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    const amplitudeIdentify = new amplitude.Identify();
    return this.amplitude!.groupIdentify(groupType, groupName, amplitudeIdentify, options);
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   */
  track(event: Event, options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.track(event, undefined, options);
  }

  /**
   * Click Event
   *
   * [View in Tracking Plan](https://data.amplitude.com/co2ai-pe/PE/events/main/latest/Click%20Event)
   *
   * Owner: Sebastian Piedoux
   *
   * @param properties The event's properties (e.g. buttonText)
   * @param options Amplitude event options.
   */
  clickEvent(
    properties: ClickEventProperties,
    options?: EventOptions,
  ) {
    return this.track(new ClickEvent(properties), options);
  }

  /**
   * Page View
   *
   * [View in Tracking Plan](https://data.amplitude.com/co2ai-pe/PE/events/main/latest/Page%20View)
   *
   * Owner: Sebastian Piedoux
   *
   * @param properties The event's properties (e.g. pageTitle)
   * @param options Amplitude event options.
   */
  pageView(
    properties: PageViewProperties,
    options?: EventOptions,
  ) {
    return this.track(new PageView(properties), options);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type BrowserOptions = amplitude.Types.BrowserOptions;

export type BrowserClient = amplitude.Types.BrowserClient;
export type BaseEvent = amplitude.Types.BaseEvent;
export type IdentifyEvent = amplitude.Types.IdentifyEvent;
export type GroupEvent = amplitude.Types.GroupIdentifyEvent;
export type Event = amplitude.Types.Event;
export type EventOptions = amplitude.Types.EventOptions;
export type Result = amplitude.Types.Result;
